import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Commons from './../utils/Commons';
import { useAuth } from './AuthContext';
import { Card, Typography, Button, Input, Checkbox } from "@material-tailwind/react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarWeek, faClock, faCheckCircle, faExclamationTriangle, faForward } from '@fortawesome/free-solid-svg-icons';

const { rootUrl } = require('../../config/config');
const common = new Commons();

function EndOfWeek({ setSuccessMessage, setFailMessage, onWeekClosed }) {
    const { user } = useAuth();
    const [, setDateFrom] = useState('');
    const [isPosting, setIsPosting] = useState(false);
    
    // Executive skip options
    const [showExecutiveOptions, setShowExecutiveOptions] = useState(false);
    const [skipWeeks, setSkipWeeks] = useState(0);
    const [confirmSkip, setConfirmSkip] = useState(false);

    const getCurrentCalendarWeek = () => {
        const currentDate = new Date();
        const year = currentDate.getFullYear();
        const week = Math.ceil(
            ((currentDate - new Date(year, 0, 1)) / 86400000 + new Date(year, 0, 1).getDay() + 1) / 7
        );
        return `${year}_${String(week).padStart(2, '0')}`; // Format as "YYYY_WW"
    };

    const [yearMonth, setYearMonth] = useState(getCurrentCalendarWeek());

    useEffect(() => {
        const fetchStoredCalendarWeek = async () => {
            const week = await common.getStoredCalendarWeek();
            setYearMonth(week);
        };

        const savedDateFrom = localStorage.getItem('date_from');
        if (savedDateFrom) {
            setDateFrom(savedDateFrom);
            console.log('Retrieved date_from from localStorage:', savedDateFrom);
        }

        fetchStoredCalendarWeek();
        console.log('Current user:', user);
    }, [user]);

    const calculateNextWeek = (currentWeek, skip = 0) => {
        let [year, week] = currentWeek.split('_').map(Number);
        week += (1 + skip);
        
        if (week > 52) {
            const overflow = week - 52;
            year += 1;
            week = overflow;
        }
        
        return `${year}_${String(week).padStart(2, '0')}`;
    };

    const handleEndWeekClick = async (useExecutive = false) => {
        const currentWeek = getCurrentCalendarWeek();

        if (!useExecutive && currentWeek === yearMonth) {
            const today = new Date().toISOString().split('T')[0];
            setDateFrom(today);
            localStorage.setItem('date_from', today);

            console.log('Updated dateFrom:', today);
            console.log('Saved in localStorage:', localStorage.getItem('date_from'));

            setFailMessage("You cannot end the week because the calendar week has not advanced ⛔.");
            setTimeout(() => setFailMessage(''), 6000);
            return;
        }

        if (useExecutive && (!confirmSkip || skipWeeks < 1)) {
            setFailMessage("Please confirm the week skip and enter a valid number of weeks to skip.");
            setTimeout(() => setFailMessage(''), 6000);
            return;
        }

        try {
            setIsPosting(true);
            
            const endpoint = useExecutive ? '/system/end_week_executive' : '/system/end_week';
            const payload = useExecutive 
                ? { input_by: user.username, skip_weeks: skipWeeks }
                : { input_by: user.username };

            const response = await axios.post(
                `${rootUrl}${endpoint}`,
                payload,
                {
                    headers: { 'Content-Type': 'application/json' },
                    withCredentials: true
                }
            );

            if (response.data.nextWeek || response.data.newWeek) {
                const newWeek = response.data.nextWeek?.week || response.data.newWeek;
                setYearMonth(newWeek);

                if (typeof onWeekClosed === 'function') {
                    try {
                        await onWeekClosed();
                    } catch (refreshError) {
                        console.error('Failed to refresh dashboard after closing week:', refreshError);
                    }
                }
                
                const successMsg = useExecutive 
                    ? `Week closed successfully! Skipped ${skipWeeks} week(s). New week: ${newWeek} ✅`
                    : "End week added successfully ✅!";
                
                setSuccessMessage(successMsg);
                
                // Reset executive options
                setShowExecutiveOptions(false);
                setSkipWeeks(0);
                setConfirmSkip(false);
            }

            setTimeout(() => setSuccessMessage(''), 6000);
        } catch (error) {
            const errorMsg = useExecutive 
                ? "Executive week skip failed!"
                : "End Week failed to add!";
            
            setFailMessage(errorMsg);
            setTimeout(() => setFailMessage(''), 6000);
            console.error('End week error:', error);
        } finally {
            setIsPosting(false);
        }
    };

    const isWeekReady = getCurrentCalendarWeek() !== yearMonth;

    return (
        <div className="w-full">
            {/* Status Overview */}
            <div className="mb-6">
                <Typography variant="h6" color="blue-gray" className="font-semibold text-brand-secondary mb-4">
                    Week Status Overview
                </Typography>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    {/* Calendar Week Display */}
                    <div className="bg-gradient-to-r from-brand-primary/5 to-brand-primary/10 rounded-lg p-4 border border-brand-primary/20">
                        <div className="flex items-center gap-2 mb-2">
                            <div className="bg-brand-primary/10 p-1.5 rounded-lg">
                                <FontAwesomeIcon icon={faCalendarWeek} className="text-brand-primary text-lg" />
                            </div>
                            <Typography variant="small" color="blue-gray" className="font-semibold text-brand-secondary">
                                Calendar Week
                            </Typography>
                        </div>
                        <Typography variant="h5" color="blue-gray" className="font-bold text-brand-primary">
                            {getCurrentCalendarWeek()}
                        </Typography>
                    </div>

                    {/* System Week Display */}
                    <div className={`rounded-lg p-4 border ${
                        isWeekReady 
                            ? 'bg-gradient-to-r from-brand-success/5 to-brand-success/10 border-brand-success/20' 
                            : 'bg-gradient-to-r from-orange-100 to-orange-200 border-orange-300'
                    }`}>
                        <div className="flex items-center gap-2 mb-2">
                            <div className={`p-1.5 rounded-lg ${
                                isWeekReady 
                                    ? 'bg-brand-success/10' 
                                    : 'bg-orange-100'
                            }`}>
                                <FontAwesomeIcon 
                                    icon={isWeekReady ? faCheckCircle : faClock} 
                                    className={`text-lg ${
                                        isWeekReady 
                                            ? 'text-brand-success' 
                                            : 'text-orange-600'
                                    }`} 
                                />
                            </div>
                            <Typography variant="small" color="blue-gray" className="font-semibold text-brand-secondary">
                                System Week
                            </Typography>
                        </div>
                        <Typography variant="h5" color="blue-gray" className={`font-bold ${
                            isWeekReady 
                                ? 'text-brand-success' 
                                : 'text-orange-600'
                        }`}>
                            {yearMonth}
                        </Typography>
                    </div>
                </div>
            </div>

            {/* Action Section */}
            <div className="border-t border-gray-200 pt-4">
                <Typography variant="h6" color="blue-gray" className="font-semibold text-brand-secondary mb-3">
                    Week Management Actions
                </Typography>
                
                <div className="flex flex-col gap-3">
                    {/* Normal End Week Button */}
                    <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                        <Button
                            className={`font-semibold px-6 py-2.5 rounded-lg transition-all duration-200 ${
                                isWeekReady 
                                    ? 'bg-brand-primary hover:bg-brand-primary/90 text-white shadow-lg hover:shadow-xl' 
                                    : 'bg-gray-400 text-white cursor-not-allowed'
                            } ${isPosting ? 'opacity-50 cursor-not-allowed' : ''}`}
                            onClick={() => handleEndWeekClick(false)}
                            disabled={isPosting || !isWeekReady}
                            size="md"
                        >
                            {isPosting ? (
                                <div className="flex items-center gap-2">
                                    <div className="spinner-brand"></div>
                                    <span>Processing...</span>
                                </div>
                            ) : (
                                <div className="flex items-center gap-2">
                                    <FontAwesomeIcon icon={faCheckCircle} />
                                    <span>End Current Week</span>
                                </div>
                            )}
                        </Button>
                        
                        {!isWeekReady && (
                            <div className="text-sm text-orange-600 bg-orange-50 px-3 py-2 rounded-lg border border-orange-200">
                                <strong>Note:</strong> Week transition will be available when the calendar week advances.
                            </div>
                        )}
                    </div>

                    {/* Executive Options Toggle */}
                    <div className="border-t pt-4 mt-2">
                        <Button
                            variant="outlined"
                            color="deep-orange"
                            onClick={() => setShowExecutiveOptions(!showExecutiveOptions)}
                            className="flex items-center gap-2"
                            size="sm"
                        >
                            <FontAwesomeIcon icon={faForward} />
                            {showExecutiveOptions ? 'Hide' : 'Show'} Executive Week Skip
                        </Button>

                        {showExecutiveOptions && (
                            <Card className="mt-4 p-4 bg-orange-50 border border-orange-200">
                                <Typography variant="h6" color="deep-orange" className="mb-3 flex items-center gap-2">
                                    <FontAwesomeIcon icon={faExclamationTriangle} />
                                    Executive Week Skip
                                </Typography>
                                
                                <div className="space-y-4">
                                    <div>
                                        <Typography variant="small" color="gray" className="mb-2">
                                            Number of weeks to skip (1-10):
                                        </Typography>
                                        <Input
                                            type="number"
                                            min="1"
                                            max="10"
                                            value={skipWeeks}
                                            onChange={(e) => setSkipWeeks(parseInt(e.target.value) || 0)}
                                            label="Weeks to skip"
                                            className="bg-white"
                                        />
                                        {skipWeeks > 0 && (
                                            <Typography variant="small" color="blue-gray" className="mt-2">
                                                Current week: <strong>{yearMonth}</strong> → 
                                                Next week will be: <strong>{calculateNextWeek(yearMonth, skipWeeks)}</strong>
                                            </Typography>
                                        )}
                                    </div>

                                    <Checkbox
                                        checked={confirmSkip}
                                        onChange={(e) => setConfirmSkip(e.target.checked)}
                                        label={
                                            <Typography variant="small" color="gray">
                                                I understand this will skip {skipWeeks} week number(s) while maintaining date continuity
                                            </Typography>
                                        }
                                    />

                                    <Button
                                        color="deep-orange"
                                        onClick={() => handleEndWeekClick(true)}
                                        disabled={isPosting || !confirmSkip || skipWeeks < 1}
                                        className="w-full flex items-center justify-center gap-2"
                                    >
                                        {isPosting ? (
                                            <>
                                                <div className="spinner-brand"></div>
                                                Processing Skip...
                                            </>
                                        ) : (
                                            <>
                                                <FontAwesomeIcon icon={faForward} />
                                                Execute Week Skip
                                            </>
                                        )}
                                    </Button>

                                    <Typography variant="small" color="red" className="text-center">
                                        ⚠️ This is an executive override. Use with caution!
                                    </Typography>
                                </div>
                            </Card>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default EndOfWeek;
